import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import ErrorIcon from "@mui/icons-material/Error";
import { IconButton, TextField } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./XplanTableStyles.css";
import Header from "../../components/Header";
import XplanTableFilters from "../../components/XplanTableFilters/XplanTableFilters.js";
import ScrollUp from "../../components/ScrollUp/ScrollUp.js";
import { UserContext } from "../../context/UserContext";
import { checkRole } from "../../util/FormUtils";
import ROLES from "../../util/roles";
import BASE_URL from "../../config/apiConfig";

const XplanTable = () => {
    const { user } = useContext(UserContext);
    const columns = [
        "Plan ID",
        "Protocol Number",
        "Title",
        "Area",
        "Lab Name",
        "Plan Status",
    ];
    const [allPlans, setAllPlans] = useState([]);
    const [xplans, setXplans] = useState([]);
    const [sort, setSort] = useState("descending");
    const [searchTerms, setSearchTerms] = useState("");
    const [filters, setFilters] = useState({});
    const [openFilterWindow, setOpenFilterWindow] = useState(false);
    const [scrollUp, setScrollUp] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        const xplansResponse = await axios.get(BASE_URL + "/api/xplans/findAllRecent",{ withCredentials: true });
        const labsResponse = await axios.get(BASE_URL + "/api/labs",{ withCredentials: true });

        let labsList = {};
        Object.values(labsResponse.data).forEach((lab) => {
            labsList[lab.Code] = lab.Name;
        });
        let plans = Object.values(xplansResponse.data).map(row => ({ ...row, labs: labsList[row.labs] || row.labs }));
        setAllPlans(plans);
        setLoading(false);
    };

    useEffect(() => {
        document.title = "Experimental Plans";
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        document.addEventListener("scroll", () => {
            if (window.pageYOffset > 300) {
                setScrollUp(true);
            } else {
                setScrollUp(false);
            }
        });
    }, []);

    useEffect(() => {
        const handleClick = () => {
            setOpenFilterWindow(false);
        };

        if (openFilterWindow) {
            document.addEventListener("click", handleClick);
        } else {
            document.removeEventListener("click", handleClick);
        }

        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, [openFilterWindow]);

    useEffect(() => {
        const containSearchTerms = (plan) => {
            const term = searchTerms.toLowerCase();

            return (
                plan.epId.toString()?.toLowerCase().includes(term) ||
                plan.title?.toLowerCase().includes(term)
            );
        };

        const matchFilters = (plan) => {
            // Exclude "Future Work" status by default
            const futureWorkRequested = filters.status?.includes("Future Work");

            if (plan.status === "Future Work" && !futureWorkRequested) {
                return false;
            }

            for (const fieldName in filters) {
                const selectedFilter = filters[fieldName];

                if (!selectedFilter?.length) {
                    continue;
                }

                if (!selectedFilter.some(fieldType => plan[fieldName]?.includes(fieldType))) {
                    return false;
                }
            }
            return true;
        };

        let filtered = allPlans.filter(containSearchTerms).filter(matchFilters);
        filtered = [...filtered].sort((a, b) => {
            if (sort === "descending") {
                return b.epId - a.epId;
            } else {
                return a.epId - b.epId;
            }
        });

        setXplans(filtered);
    }, [allPlans, searchTerms, filters, sort]);

    const sortOrder = () => {
        setSort(prev => prev === "descending" ? "ascending" : "descending");
    };

    const handleSearch = (e) => {
        setSearchTerms(e.target.value);
    }

    const handleSearchClear = () => {
        setSearchTerms("");
    }

    const statusColor = (status) => {
        switch (status) {
            case "Completed":
                return "statusCompleted";
            case "Open":
                return "statusOpen";
            case "Pending Documentation":
                return "statusPendingDocumentation";
            case "Pending Approval":
                return "statusPendingApproval";
            case "Plan Approved":
                return "statusPlanApproved";
            case "Real Time":
                return "statusRealTime";
            case "Future Work":
                return "statusFutureWork";
            case "Withdrawn":
                return "statusWithdrawn";
            case "Archived":
                return "statusArchived";
        }
    }

    return (
        <div>
            <Header/>
            {loading ? (
                <div className="loader center">
                    <i className="fa fa-cog fa-spin"/>
                </div>
            ) : (
                <>
                    {scrollUp && <ScrollUp/>}
                    <main className="dataTableSection">
                        <div className="dataActions">
                            <div className="dataSearch">
                                <TextField
                                    className="dataSearchField"
                                    placeholder="Plan ID or Title"
                                    value={searchTerms}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: <SearchIcon className="dataSearchIcon" fontSize="small"/>,
                                        endAdornment: searchTerms.length ? (
                                            <IconButton onClick={() => handleSearchClear()}>
                                                <ClearIcon className="dataClearIcon" fontSize="small"/>
                                            </IconButton>
                                        ) : null,
                                    }}
                                    sx={{ fontSize: "15px", height: "48px", width: "576px", padding: 0, margin: 0 }}
                                />
                                <div className="dataTableResults">
                                    <span>Results: </span>{xplans.length}
                                </div>
                            </div>
                            <div className="dataSortFilterAdd">
                                <a
                                    className="dataFilter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenFilterWindow((prev) => !prev);
                                    }}
                                    style={{ backgroundColor: Object.values(filters).some(value => !!value && value.length > 0) ? "#cceeff" : "inherit" }}
                                >
                                    <span className="dataActionIcon">
                                        <FilterListIcon/>
                                    </span>
                                    <span className="dataActionName">Filters</span>
                                </a>
                                {openFilterWindow && (
                                    <XplanTableFilters
                                        filters={filters}
                                        setFilters={setFilters}
                                        xplans={allPlans}
                                    />
                                )}
                                <a className="dataAdd" href="/xplans/new">
                                    <span className="dataActionIcon">
                                        <AddIcon/>
                                    </span>
                                    <span className="dataActionName">New Plan</span>
                                </a>
                            </div>
                        </div>
                        <div className="dataTable">
                            <div className="dataTableHeader">
                                {columns.map((column, index) => (
                                    <p key={index} className="dataTableFieldTitle">
                                        <span>{column}</span>
                                        {column === "Plan ID" && (
                                            <button className="sortButton" onClick={sortOrder}>
                                                {sort === "descending" ? (
                                                    <ArrowDownwardIcon fontSize="small"/>
                                                ) : (
                                                    <ArrowUpwardIcon fontSize="small"/>
                                                )}
                                            </button>
                                        )}
                                    </p>
                                ))}
                            </div>
                            {xplans.length ? (
                                <div className="dataTableBody">
                                    {xplans.map((xplan, index) => {
                                        return xplan.epId === 0 && !checkRole(user, [ROLES.ADMIN]) ? null : (
                                            <a
                                                key={index}
                                                className="dataTableBodyRow"
                                                href={`/xplans/${xplan.epId}`}
                                            >
                                                <p className="dataTableId">
                                                    <span>{xplan.epId}</span>
                                                </p>
                                                <p className="dataTableProtocol">
                                                    <span>{xplan.protocol ? xplan.protocol.replaceAll(",", ", ") : xplan.protocol}</span>
                                                </p>
                                                <p className="dataTableTitle">{xplan.title}</p>
                                                <p className="dataTableArea">
                                                    {xplan.area?.split(",").map((area, i) => (
                                                        <span key={i}>{area}</span>
                                                    ))}
                                                </p>
                                                <p className="dataTableLab">{xplan.labs}</p>
                                                <div
                                                    className={`dataTableStatus ${statusColor(xplan.status)} ${xplan.status}`}>
                                                    <p>
                                                        <span className="dotIcon">&#8226; </span>
                                                        <span className="statusText">{xplan.status}</span>
                                                    </p>
                                                </div>
                                            </a>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="dataTableNotFound">
                                    <ErrorIcon fontSize="small"/>
                                    <p>No plans found</p>
                                </div>
                            )}
                        </div>
                    </main>
                </>
            )}
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
        </div>
    );
}

export default XplanTable;
